import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Alert,
  Button
} from '@mui/material';
import { CheckCircle as CheckCircleIcon, Error as ErrorIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const ShopeeCallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  
  const { authenticateShopee } = useAuth();
  const navigate = useNavigate();
  // Shopee auth codes are single-use. React StrictMode runs effects twice in dev,
  // which would exchange the same code twice — the second call fails with a 500.
  // Guard so the exchange runs only once.
  const hasRun = useRef(false);

  useEffect(() => {
    const code = searchParams.get('code');

    if (!code) {
      setStatus('error');
      setErrorMessage('Authorization code is missing');
      return;
    }

    if (hasRun.current) {
      return;
    }
    hasRun.current = true;

    const handleAuth = async () => {
      try {
        await authenticateShopee(code);
        setStatus('success');
        // Auto-redirect after success
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } catch (error) {
        console.error('Shopee authentication failed:', error);
        setStatus('error');
        setErrorMessage(error instanceof Error ? error.message : 'Authentication failed');
      }
    };
    
    handleAuth();
  }, [searchParams, authenticateShopee, navigate]);
  
  return (
    <Box 
      sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        backgroundColor: '#f5f5f5'
      }}
    >
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          maxWidth: 500, 
          width: '100%',
          borderRadius: 2,
          textAlign: 'center'
        }}
      >
        <Typography variant="h5" component="h1" gutterBottom>
          Shopee Authentication
        </Typography>
        
        {status === 'loading' && (
          <Box sx={{ my: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Typography variant="body1">
              Processing your authentication...
            </Typography>
          </Box>
        )}
        
        {status === 'success' && (
          <Box sx={{ my: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="body1" gutterBottom>
              Successfully connected to Shopee!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Redirecting you to the dashboard in a few seconds...
            </Typography>
          </Box>
        )}
        
        {status === 'error' && (
          <Box sx={{ my: 4 }}>
            <Alert 
              severity="error" 
              icon={<ErrorIcon fontSize="large" />}
              sx={{ mb: 3 }}
            >
              <Typography variant="subtitle1" gutterBottom>
                Authentication Failed
              </Typography>
              <Typography variant="body2">
                {errorMessage || 'An unexpected error occurred during authentication.'}
              </Typography>
            </Alert>
            
            <Button
              variant="contained"
              onClick={() => navigate('/settings')}
              fullWidth
            >
              Return to Settings
            </Button>
          </Box>
        )}
        
        {status === 'success' && (
          <Button
            variant="outlined"
            onClick={() => navigate('/')}
            sx={{ mt: 2 }}
          >
            Go to Dashboard Now
          </Button>
        )}
      </Paper>
    </Box>
  );
};

export default ShopeeCallbackPage;